import { CalendarMonthData, CalendarTimeEntry, RecurringTimeEntry } from '../../services/types';
import { RecurringProgressEntry } from '../../services/api';
import { normalizeCompanyName } from './calendarUtils';
import { getFrequencyDatesForMonth } from '../../utils/recurringEntryUtils';

// ─── Types ───────────────────────────────────────────────────────────────────

export type GapStatus = 'missing' | 'partial' | 'on_track';

export interface TemplateGap {
  template: RecurringTimeEntry;
  expectedCount: number;
  appliedCount: number;
  missingDates: string[];
}

/** A single time entry summary for the detail expansion */
export interface EntryDetail {
  id: string;
  dateWorked: string;
  hoursWorked: number;
  ticketNumber: string;
  summaryNotes: string;
}

export interface CompanyGap {
  companyName: string;          // display name (shortest variant)
  companyId: number | null;
  hoursByMonth: [number, number, number]; // [m-2, m-1, current]
  priorAverage: number;
  status: GapStatus;
  linkedTemplates: TemplateGap[];
  /** Entries per month for detail expansion */
  entriesByMonth: [EntryDetail[], EntryDetail[], EntryDetail[]];
}

export interface MonthLabel {
  year: number;
  month: number; // 0-indexed
  label: string; // e.g. "Dec '25"
}

export interface CompareData {
  months: [MonthLabel, MonthLabel, MonthLabel];
  gaps: {
    missing: CompanyGap[];
    partial: CompanyGap[];
    onTrack: CompanyGap[];
  };
  totals: {
    /** Actual hours per month across all tracked companies */
    monthHours: [number, number, number];
    coveragePercent: number;
    gapCount: number;
    templateGapCount: number;
  };
  hasLimitedHistory: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function monthLabel(year: number, month: number): MonthLabel {
  return { year, month, label: `${SHORT_MONTHS[month]} '${String(year).slice(2)}` };
}

/**
 * Enhanced normalization for compare grouping:
 * - Strips trailing parenthetical abbreviations like "(LHMWD)" and "(DBA ...)"
 * - Strips ALL common legal suffixes (Inc, LLC, LLP, Ltd, Corp, Co, PC, PLLC)
 * - Case-insensitive
 * This ensures "Slovak Baron Empey Murphy & Pinkney LLP" and
 * "SLOVAK BARON EMPEY MURPHY & PINKNEY, LLP" produce the same key.
 */
function compareNormalize(name: string | undefined | null): string {
  const base = normalizeCompanyName(name);
  return base
    .replace(/\s*\([^)]*\)\s*$/, '')  // strip trailing parenthetical
    .replace(/[,.\s]+(inc|llc|llp|ltd|pllc|pc|corp|co)\.?\s*$/i, '')  // strip legal suffixes
    .trim()
    .toLowerCase();
}

interface CompanyMonthStats {
  hours: number;
  count: number;
  companyId: number | null;
  displayName: string; // shortest name variant seen
  entries: EntryDetail[];
}

function groupByCompany(entries: CalendarTimeEntry[]): Map<string, CompanyMonthStats> {
  const map = new Map<string, CompanyMonthStats>();
  for (const e of entries) {
    const key = compareNormalize(e.companyName);
    if (!key) continue;
    const detail: EntryDetail = {
      id: e.id,
      dateWorked: e.dateWorked,
      hoursWorked: e.hoursWorked,
      ticketNumber: e.ticketNumber,
      summaryNotes: e.summaryNotes,
    };
    const existing = map.get(key);
    if (existing) {
      existing.hours += e.hoursWorked;
      existing.count += 1;
      existing.entries.push(detail);
      if (!existing.companyId && e.companyId) existing.companyId = e.companyId;
      // Keep the shortest display name
      if (e.companyName && e.companyName.length < existing.displayName.length) {
        existing.displayName = e.companyName;
      }
    } else {
      map.set(key, {
        hours: e.hoursWorked,
        count: 1,
        companyId: e.companyId || null,
        displayName: e.companyName || key,
        entries: [detail],
      });
    }
  }
  return map;
}

// ─── Main Analysis ───────────────────────────────────────────────────────────

export function analyzeGaps(
  monthData: [CalendarMonthData | null, CalendarMonthData | null, CalendarMonthData | null],
  currentDate: Date,
  templates: RecurringTimeEntry[],
  progress: Record<string, RecurringProgressEntry>,
  companyAliases?: string[][]
): CompareData {
  const curYear = currentDate.getFullYear();
  const curMonth = currentDate.getMonth();

  // Build month labels (m-2, m-1, current)
  const m2Date = new Date(curYear, curMonth - 2, 1);
  const m1Date = new Date(curYear, curMonth - 1, 1);
  const months: [MonthLabel, MonthLabel, MonthLabel] = [
    monthLabel(m2Date.getFullYear(), m2Date.getMonth()),
    monthLabel(m1Date.getFullYear(), m1Date.getMonth()),
    monthLabel(curYear, curMonth),
  ];

  // Group entries by company for each month
  const rawByMonth = monthData.map(md => groupByCompany(md?.entries ?? []));

  // Merge company name variants: if one normalized key is a prefix of another
  // (e.g. "gosch ford" / "gosch ford temecula"), merge them under the shorter key.
  const allKeys = new Set<string>();
  for (const m of rawByMonth) {
    for (const key of m.keys()) allKeys.add(key);
  }
  const sortedKeys = [...allKeys].sort((a, b) => a.length - b.length);
  const mergeMap = new Map<string, string>(); // long key → canonical short key
  for (let i = 0; i < sortedKeys.length; i++) {
    const short = sortedKeys[i];
    if (mergeMap.has(short)) continue; // already merged into something shorter
    if (short.length < 5) continue; // too short to prefix-match safely
    for (let j = i + 1; j < sortedKeys.length; j++) {
      const long = sortedKeys[j];
      if (mergeMap.has(long)) continue;
      if (long.startsWith(short) && (long[short.length] === ' ' || long[short.length] === ',' || long[short.length] === '.')) {
        mergeMap.set(long, short);
      }
    }
  }

  // Alias-based merge: merge company name variants from companyAliases setting
  if (companyAliases && companyAliases.length > 0) {
    for (const aliasGroup of companyAliases) {
      if (aliasGroup.length < 2) continue;
      // Normalize each alias and find which keys exist in our data
      const normalizedAliases = aliasGroup.map(a => compareNormalize(a));
      // Resolve through existing mergeMap (an alias target may already be merged)
      const resolvedKeys = normalizedAliases.map(a => mergeMap.get(a) ?? a);
      // Find which of these actually exist in allKeys
      const existingKeys = [...new Set(resolvedKeys)].filter(k => allKeys.has(k));
      if (existingKeys.length < 2) continue;
      // Pick the shortest as canonical
      const canonical = existingKeys.sort((a, b) => a.length - b.length)[0];
      for (const key of existingKeys) {
        if (key !== canonical) {
          mergeMap.set(key, canonical);
        }
      }
      // Also map the original normalized aliases that weren't already mapped
      for (const na of normalizedAliases) {
        if (!mergeMap.has(na) && na !== canonical && allKeys.has(na)) {
          mergeMap.set(na, canonical);
        }
      }
    }
  }

  // companyId-based merge: if entries across months share the same companyId
  // but have different normalized names, merge them under one key.
  // This catches name differences between sources (TimeTech vs AutoTask).
  const idToKey = new Map<number, string>(); // companyId → first seen canonical key
  for (const m of rawByMonth) {
    for (const [key, stats] of m) {
      const cid = stats.companyId;
      if (!cid || cid === 0) continue;
      const canonical = mergeMap.get(key) ?? key;
      const existingKey = idToKey.get(cid);
      if (existingKey && existingKey !== canonical) {
        // Same companyId, different key — merge the longer into the shorter
        const [shorter, longer] = existingKey.length <= canonical.length
          ? [existingKey, canonical]
          : [canonical, existingKey];
        if (!mergeMap.has(longer) || mergeMap.get(longer) !== shorter) {
          mergeMap.set(longer, shorter);
          idToKey.set(cid, shorter);
        }
      } else if (!existingKey) {
        idToKey.set(cid, canonical);
      }
    }
  }

  // Apply merges to each month's data
  const byMonth = rawByMonth.map(monthMap => {
    const merged = new Map<string, CompanyMonthStats>();
    for (const [key, stats] of monthMap) {
      const canonical = mergeMap.get(key) ?? key;
      const existing = merged.get(canonical);
      if (existing) {
        existing.hours += stats.hours;
        existing.count += stats.count;
        existing.entries.push(...stats.entries);
        if (!existing.companyId && stats.companyId) existing.companyId = stats.companyId;
        if (stats.displayName.length < existing.displayName.length) {
          existing.displayName = stats.displayName;
        }
      } else {
        merged.set(canonical, { ...stats, entries: [...stats.entries] });
      }
    }
    return merged;
  });

  // Determine available history
  const hasM2 = (monthData[0]?.entries?.length ?? 0) > 0;
  const hasM1 = (monthData[1]?.entries?.length ?? 0) > 0;
  const hasLimitedHistory = !hasM2 && !hasM1;

  // Union all company names (from merged data)
  const allCompanies = new Set<string>();
  for (const m of byMonth) {
    for (const key of m.keys()) allCompanies.add(key);
  }

  // Build template lookup by company (compareNormalize + merge → templates)
  const templatesByCompany = new Map<string, RecurringTimeEntry[]>();
  for (const t of templates) {
    if (!t.isActive) continue;
    if (t.templateType === 'client' && t.companyName) {
      const rawKey = compareNormalize(t.companyName);
      const key = mergeMap.get(rawKey) ?? rawKey;
      const existing = templatesByCompany.get(key) ?? [];
      existing.push(t);
      templatesByCompany.set(key, existing);
    } else if (t.templateType === 'activity' && t.clients) {
      for (const c of t.clients) {
        const rawKey = compareNormalize(c.companyName);
        const key = mergeMap.get(rawKey) ?? rawKey;
        const existing = templatesByCompany.get(key) ?? [];
        if (!existing.includes(t)) existing.push(t);
        templatesByCompany.set(key, existing);
      }
    }
  }

  // Analyze each company
  const gaps: CompanyGap[] = [];
  for (const companyKey of allCompanies) {
    const s0 = byMonth[0].get(companyKey);
    const s1 = byMonth[1].get(companyKey);
    const s2 = byMonth[2].get(companyKey);

    const h0 = s0?.hours ?? 0;
    const h1 = s1?.hours ?? 0;
    const h2 = s2?.hours ?? 0;

    // Pick the best display name (shortest from any month)
    const names = [s0?.displayName, s1?.displayName, s2?.displayName].filter(Boolean) as string[];
    const displayName = names.sort((a, b) => a.length - b.length)[0] || companyKey;

    // Determine company ID from any month that has it
    const companyId = s2?.companyId ?? s1?.companyId ?? s0?.companyId ?? null;

    // Calculate prior average (use available months)
    const priorMonths: number[] = [];
    if (hasM2 && h0 > 0) priorMonths.push(h0);
    if (hasM1 && h1 > 0) priorMonths.push(h1);

    // Skip if no prior history at all (company only in current month)
    if (priorMonths.length === 0) continue;

    const priorAvg = priorMonths.reduce((a, b) => a + b, 0) / priorMonths.length;

    // Filter noise: skip tiny prior averages
    if (priorAvg < 0.5) continue;

    // Classify gap status
    let status: GapStatus;
    if (h2 === 0) {
      status = 'missing';
    } else if (h2 < priorAvg * 0.5) {
      status = 'partial';
    } else {
      status = 'on_track';
    }

    // Find linked templates and their gaps
    const companyTemplates = templatesByCompany.get(companyKey) ?? [];
    const linkedTemplates: TemplateGap[] = [];
    for (const t of companyTemplates) {
      const expectedDates = getFrequencyDatesForMonth(t, curYear, curMonth);
      const prog = progress[t.id];
      const appliedCount = prog?.applied ?? 0;
      const appliedDates = new Set(Object.keys(prog?.entryCounts ?? {}));
      const missingDates = expectedDates.filter(d => !appliedDates.has(d));

      if (missingDates.length > 0) {
        linkedTemplates.push({
          template: t,
          expectedCount: expectedDates.length,
          appliedCount,
          missingDates,
        });
      }
    }

    gaps.push({
      companyName: displayName,
      companyId,
      hoursByMonth: [h0, h1, h2],
      priorAverage: priorAvg,
      status,
      linkedTemplates,
      entriesByMonth: [
        (s0?.entries ?? []).sort((a, b) => a.dateWorked.localeCompare(b.dateWorked)),
        (s1?.entries ?? []).sort((a, b) => a.dateWorked.localeCompare(b.dateWorked)),
        (s2?.entries ?? []).sort((a, b) => a.dateWorked.localeCompare(b.dateWorked)),
      ],
    });
  }

  // Sort each category: highest prior avg first
  const missing = gaps.filter(g => g.status === 'missing').sort((a, b) => b.priorAverage - a.priorAverage);
  const partial = gaps.filter(g => g.status === 'partial').sort((a, b) => b.priorAverage - a.priorAverage);
  const onTrack = gaps.filter(g => g.status === 'on_track').sort((a, b) => b.priorAverage - a.priorAverage);

  // Compute per-month totals (across all tracked companies)
  const monthHours: [number, number, number] = [
    gaps.reduce((sum, g) => sum + g.hoursByMonth[0], 0),
    gaps.reduce((sum, g) => sum + g.hoursByMonth[1], 0),
    gaps.reduce((sum, g) => sum + g.hoursByMonth[2], 0),
  ];
  const priorAvgTotal = ((monthHours[0] || 0) + (monthHours[1] || 0)) / (hasM2 && hasM1 ? 2 : 1);
  const coveragePercent = priorAvgTotal > 0 ? (monthHours[2] / priorAvgTotal) * 100 : 100;
  const templateGapCount = gaps.reduce((sum, g) => sum + g.linkedTemplates.length, 0);

  return {
    months,
    gaps: { missing, partial, onTrack },
    totals: {
      monthHours,
      coveragePercent: Math.min(coveragePercent, 100),
      gapCount: missing.length + partial.length,
      templateGapCount,
    },
    hasLimitedHistory,
  };
}
